import React, { useState, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
} from "@mui/material";
import {
    Upload,
    FileSpreadsheet,
    Download,
    X,
    CloudUpload,
    CheckCircle,
    AlertTriangle,
    AlertCircle
} from "lucide-react";
import * as XLSX from "xlsx";
import { dealsAPI } from "../services/api";
import { downloadLeadImportTemplate } from "../utils/leadImportTemplate";

const UploadSheetModal = ({ open, onClose, onUploadComplete, pipeline }) => {
    const [file, setFile] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });
    const [dragActive, setDragActive] = useState(false);

    // View State: 'upload' | 'result'
    const [view, setView] = useState("upload");
    const [result, setResult] = useState(null);
    const [downloading, setDownloading] = useState(false);

    const fileInputRef = useRef(null);
    const userId = localStorage.getItem("userId");

    const processFile = (selectedFile) => {
        if (!selectedFile) return;

        const validTypes = [
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-excel",
            "text/csv"
        ];

        const filename = selectedFile.name.toLowerCase();
        const isValidExt = filename.endsWith('.xlsx') || filename.endsWith('.xls') || filename.endsWith('.csv');

        if (!isValidExt && !validTypes.includes(selectedFile.type)) {
            setMessage({ type: "error", text: "Invalid file type. Please upload .xlsx, .xls, or .csv" });
            return;
        }

        setFile(selectedFile);
        setMessage({ type: "", text: "" });
        setView("upload");

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: "binary" });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];

                const data = XLSX.utils.sheet_to_json(ws);

                if (data.length === 0) {
                    setMessage({ type: "error", text: "Sheet is empty or invalid format." });
                    setPreviewData([]);
                    return;
                }

                setPreviewData(data);
            } catch (err) {
                console.error(err);
                setMessage({ type: "error", text: "Failed to parse file." });
            }
        };
        reader.readAsBinaryString(selectedFile);
    };

    const handleFileChange = (e) => {
        processFile(e.target.files[0]);
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleButtonClick = () => {
        fileInputRef.current.click();
    };

    const handleUpload = async () => {
        if (!previewData.length) return;

        try {
            setUploading(true);
            setMessage({ type: "", text: "" });

            const response = await dealsAPI.bulkCreate({
                userId,
                deals: previewData,
                pipeline
            });

            if (response.data.success) {
                setResult(response.data);
                setView("result"); // Switch to Result View

                // Trigger refresh if we created anything
                if (response.data.createdCount > 0 && onUploadComplete) {
                    onUploadComplete();
                }
            }
        } catch (error) {
            console.error("Upload error:", error);
            setMessage({ type: "error", text: error.response?.data?.message || "Upload failed." });
        } finally {
            setUploading(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            setDownloading(true);
            await downloadLeadImportTemplate();
        } catch (error) {
            console.error("Template download failed:", error);
            setMessage({ type: "error", text: "Couldn't create the template. Please try again." });
        } finally {
            setDownloading(false);
        }
    };

    const resetState = () => {
        setFile(null);
        setPreviewData([]);
        setMessage({ type: "", text: "" });
        setDragActive(false);
        setView("upload");
        setResult(null);
    };

    const handleClose = () => {
        if (uploading) return;
        resetState();
        onClose();
    };

    const handleRemoveFile = () => {
        setFile(null);
        setPreviewData([]);
        setMessage({ type: "", text: "" });
    };

    // Render Result View
    const renderResultView = () => {
        if (!result) return null;

        const { createdCount, errors, warnings = [] } = result;
        const duplicatesCount = errors.filter(e => e.error && e.error.toLowerCase().includes("duplicate")).length;
        const otherErrorsCount = errors.length - duplicatesCount;
        const totalProcessed = createdCount + errors.length;

        let statusColor = "#10B981"; // Success Green
        let StatusIcon = CheckCircle;
        let mainMsg = "Import Complete";

        if (createdCount === 0 && duplicatesCount === 0 && errors.length > 0) {
            statusColor = "#EF4444"; // Error Red
            StatusIcon = AlertCircle;
            mainMsg = "Import Failed";
        } else if (createdCount === 0 && duplicatesCount > 0) {
            statusColor = "#3B82F6"; // Info Blue
            StatusIcon = AlertCircle; // Or Info icon
            mainMsg = "No New Leads Added";
        } else if (errors.length > 0) {
            statusColor = "#F59E0B"; // Warning Orange
            StatusIcon = AlertTriangle;
            mainMsg = "Imported with Warnings";
        }

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                <Box sx={{ p: 1, borderRadius: '50%', mb: 0 }}>
                    <StatusIcon size={20} color={statusColor} />
                </Box>

                <Typography variant="h6" align="center" gutterBottom>
                    {mainMsg}
                </Typography>

                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                    Processed {totalProcessed} rows
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, width: '100%', mb: 3 }}>
                    <Paper sx={{ flex: 1, p: 2, textAlign: 'center', bgcolor: '#F9FAFB', border: '1px solid #E5E7EB' }} elevation={0}>
                        <Typography variant="h4" color="#10B981" fontWeight="600">{createdCount}</Typography>
                        <Typography variant="caption" color="text.secondary">Created</Typography>
                    </Paper>
                    <Paper sx={{ flex: 1, p: 2, textAlign: 'center', bgcolor: '#F9FAFB', border: '1px solid #E5E7EB' }} elevation={0}>
                        <Typography variant="h4" color="#F59E0B" fontWeight="600">{duplicatesCount}</Typography>
                        <Typography variant="caption" color="text.secondary">Skipped Duplicates</Typography>
                    </Paper>
                    {otherErrorsCount > 0 && (
                        <Paper sx={{ flex: 1, p: 2, textAlign: 'center', bgcolor: '#F9FAFB', border: '1px solid #E5E7EB' }} elevation={0}>
                            <Typography variant="h4" color="#EF4444" fontWeight="600">{otherErrorsCount}</Typography>
                            <Typography variant="caption" color="text.secondary">Errors</Typography>
                        </Paper>
                    )}
                </Box>

                {errors.length > 0 && (
                    <Box sx={{ width: '100%' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Details:</Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 150, border: "1px solid #E5E7EB", boxShadow: "none" }}>
                            <Table size="small" stickyHeader>
                                <TableBody>
                                    {errors.map((err, i) => (
                                        <TableRow key={i}>
                                            <TableCell sx={{ color: err.error.includes("Duplicate") ? "#F59E0B" : "#EF4444", fontSize: 12 }}>
                                                {err.error}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                                                Row {err.index + 1}: {[err.row["First Name"], err.row["Last Name"]].filter(Boolean).join(" ") || err.row["Deal Name"] || err.row["Name"] || err.row["Phone Numbers"] || err.row["Mobile"] || "Unknown"}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {warnings.length > 0 && (
                    <Alert severity="warning" sx={{ width: "100%", mt: 2, fontSize: 12.5 }}>
                        {warnings.length} lead{warnings.length > 1 ? "s were" : " was"} imported with a note:
                        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, maxHeight: 120, overflowY: "auto" }}>
                            {warnings.map((w, i) => (
                                <li key={i}>Row {w.index + 1}: {w.warning}</li>
                            ))}
                        </Box>
                    </Alert>
                )}
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #f0f0f0", pb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: "#EEF2FF", display: 'flex' }}>
                        <CloudUpload size={22} color="#6366F1" />
                    </Box>
                    <Typography variant="h6" fontWeight="600">
                        {view === 'result' ? 'Import Summary' : 'Import Leads'}
                    </Typography>
                </Box>
                {!uploading && (
                    <IconButton onClick={handleClose} size="small">
                        <X size={20} />
                    </IconButton>
                )}
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>

                {view === 'result' ? renderResultView() : (
                    <>
                        {!file ? (
                            <Box
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={handleButtonClick}
                                sx={{
                                    border: `2px dashed ${dragActive ? "#6366F1" : "#E5E7EB"}`,
                                    borderRadius: 3,
                                    bgcolor: dragActive ? "#F5F7FF" : "#F9FAFB",
                                    py: 6,
                                    px: 3,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 1.5,
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        borderColor: "#6366F1",
                                        bgcolor: "#F5F7FF"
                                    }
                                }}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    hidden
                                    accept=".xlsx, .xls, .csv"
                                    onChange={handleFileChange}
                                />

                                <Box sx={{ p: 2, bgcolor: "#EFF4FF", borderRadius: "50%", mb: 1 }}>
                                    <FileSpreadsheet size={32} color="#6366F1" />
                                </Box>

                                <Typography variant="body1" fontWeight="500">
                                    Click to upload or drag & drop
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    XLSX, XLS or CSV (max 5MB)
                                </Typography>

                                <Button
                                    variant="text"
                                    onClick={(e) => { e.stopPropagation(); handleDownloadTemplate(); }}
                                    disabled={downloading}
                                    startIcon={downloading ? <CircularProgress size={12} /> : <Download size={14} />}
                                    sx={{ mt: 1, textTransform: 'none', fontSize: 13, color: '#6B7280' }}
                                >
                                    Download Template
                                </Button>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{
                                    p: 2,
                                    border: "1px solid #E5E7EB",
                                    borderRadius: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    bgcolor: "#F9FAFB"
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <FileSpreadsheet size={24} color="#6366F1" />
                                        <Box>
                                            <Typography variant="body2" fontWeight="600">{file.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {(file.size / 1024).toFixed(1)} KB • {previewData.length} rows found
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <IconButton size="small" onClick={handleRemoveFile}>
                                        <X size={18} />
                                    </IconButton>
                                </Box>

                                {previewData.length > 0 && (
                                    <TableContainer component={Paper} sx={{ maxHeight: 250, border: "1px solid #E5E7EB", boxShadow: "none", borderRadius: 2 }}>
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow>
                                                    {Object.keys(previewData[0] || {}).slice(0, 3).map((header) => (
                                                        <TableCell key={header} sx={{ bgcolor: "#F9FAFB", fontWeight: 600, fontSize: 12 }}>{header}</TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {previewData.slice(0, 5).map((row, i) => (
                                                    <TableRow key={i}>
                                                        {Object.values(row).slice(0, 3).map((cell, j) => (
                                                            <TableCell key={j} sx={{ fontSize: 12 }}>{cell}</TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                        {previewData.length > 5 && (
                                            <Box sx={{ p: 1, textAlign: 'center', bgcolor: "#F9FAFB", borderTop: "1px solid #E5E7EB" }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    + {previewData.length - 5} more rows
                                                </Typography>
                                            </Box>
                                        )}
                                    </TableContainer>
                                )}
                            </Box>
                        )}

                        {message.text && (
                            <Alert severity={message.type === "error" ? "error" : (message.type === "warning" ? "warning" : "success")} sx={{ borderRadius: 2, mt: 2 }}>
                                {message.text}
                            </Alert>
                        )}
                    </>
                )}

            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 1 }}>
                {view === 'result' ? (
                    <>
                        <Button onClick={resetState} sx={{ color: "#6B7280", textTransform: "none" }}>
                            Import Another
                        </Button>
                        <Button
                            onClick={handleClose}
                            variant="contained"
                            disableElevation
                            sx={{
                                bgcolor: "#6366F1",
                                "&:hover": { bgcolor: "#4F46E5" },
                                textTransform: "none",
                                borderRadius: 2
                            }}
                        >
                            Done
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={handleClose} disabled={uploading} sx={{ color: "#6B7280", textTransform: "none", fontWeight: 500 }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUpload}
                            disabled={!file || !previewData.length || uploading}
                            variant="contained"
                            disableElevation
                            sx={{
                                bgcolor: "#6366F1",
                                "&:hover": { bgcolor: "#4F46E5" },
                                textTransform: "none",
                                borderRadius: 2,
                                fontWeight: 600,
                                minWidth: 120
                            }}
                        >
                            {uploading ? <CircularProgress size={20} color="inherit" /> : 'Import Leads'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default UploadSheetModal;
